import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiSave } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import { applicationOptionLabel, champFields, extractApplications, extractEntity, extractList } from '../utilities/tournamentsApi';
import {
  extractPlayer,
  playerCardPath,
  playerDisplayName,
  playerShirtNumber,
} from '../utilities/playersApi';
import TeamPicker from '../components/TeamPicker';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/seasonRoster.css';
import '../styles/playersManagement.css';

const emptyForm = {
  team_id: '',
  team_name: '',
  application_id: '',
  application_name: '',
  number: '',
};

export default function ManagePlayerApplication() {
  const { playerId, teamId: teamContextId, appTeamId, applicationId } = useParams();
  const isChange = Boolean(appTeamId && applicationId);
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('players');

  const [form, setForm] = useState(emptyForm);
  const [playerName, setPlayerName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [applications, setApplications] = useState([]);
  const [originalAppId, setOriginalAppId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingApps, setLoadingApps] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  const goBackToPlayer = () => {
    history.push(playerCardPath(playerId, teamContextId));
  };

  useEffect(() => {
    if (!champId && champsLoading) return;
    initPage();
  }, [playerId, teamContextId, appTeamId, applicationId, champId, champsLoading]);

  useEffect(() => {
    if (!champId && champsLoading) return;
    if (!champId) return;
    loadApplications();
  }, [champId, champsLoading]);

  const initPage = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const [playerResponse, teamResponse] = await Promise.all([
        authorizedPost(dispatch, API_URLS.getAdminPlayer, {
          id: Number(playerId),
          player_id: Number(playerId),
        }),
        teamContextId
          ? authorizedPost(dispatch, API_URLS.getCatalogTeam, { id: Number(teamContextId) }).catch(() => null)
          : Promise.resolve(null),
      ]);
      const player = extractPlayer(playerResponse.data);
      setPlayerName(playerDisplayName(player) || t('plEditPlayer'));
      if (teamResponse) {
        setTeamName(extractEntity(teamResponse.data).name || '');
      }

      if (isChange) {
        const current = (player.teams || []).find((item) => (
          Number(item.team_id) === Number(appTeamId)
          && Number(item.application_id) === Number(applicationId)
        ));
        setOriginalAppId(String(applicationId));
        setForm({
          team_id: String(appTeamId),
          team_name: current?.team_name || (Number(appTeamId) === Number(teamContextId) ? (extractEntity(teamResponse?.data).name || '') : ''),
          application_id: String(current?.application_id || applicationId),
          application_name: current?.application_name || '',
          number: current?.number != null ? String(current.number) : '',
        });
      } else {
        setOriginalAppId('');
        setForm(emptyForm);
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('pdLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadApplications = async () => {
    setLoadingApps(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminApplications, champFields(champId));
      const apps = extractList(response.data);
      setApplications(apps.length ? apps : extractApplications(response.data));
    } catch (error) {
      setApplications([]);
    } finally {
      setLoadingApps(false);
    }
  };

  const handleSave = async () => {
    if (!form.team_id || !form.application_id) {
      setMessage({ text: t('plAppPickRequired'), type: 'error' });
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const number = playerShirtNumber(form.number);
      const base = {
        id: Number(playerId),
        player_id: Number(playerId),
        team_id: Number(form.team_id),
      };

      if (isChange && String(form.application_id) !== String(originalAppId)) {
        await authorizedPost(dispatch, API_URLS.changePlayerApplication, {
          ...base,
          action: 'remove',
          application_id: Number(originalAppId),
        });
        const addPayload = {
          ...base,
          action: 'add',
          application_id: Number(form.application_id),
        };
        if (number != null) addPayload.number = number;
        await authorizedPost(dispatch, API_URLS.changePlayerApplication, addPayload);
      } else {
        const payload = {
          ...base,
          action: isChange ? 'change' : 'add',
          application_id: Number(form.application_id),
        };
        if (isChange || number != null) payload.number = number != null ? number : 0;
        await authorizedPost(dispatch, API_URLS.changePlayerApplication, payload);
      }
      goBackToPlayer();
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('plAppSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const crumbs = teamContextId
    ? [
      { label: t('navTeams'), to: '/teams_management' },
      { label: teamName || t('tmEditTeam'), to: `/teams_management/${teamContextId}` },
      { label: playerName || t('plEditPlayer'), to: playerCardPath(playerId, teamContextId) },
      { label: isChange ? t('plChangeApp') : t('plAddApp') },
    ]
    : [
      { label: t('navPlayers'), to: '/players_management' },
      { label: playerName || t('plEditPlayer'), to: playerCardPath(playerId) },
      { label: isChange ? t('plChangeApp') : t('plAddApp') },
    ];

  const applicationOptions = (() => {
    const list = applications.slice();
    const currentId = String(form.application_id || '');
    if (currentId && !list.some((app) => String(app.application_id || app.id) === currentId)) {
      list.unshift({
        id: form.application_id,
        application_id: form.application_id,
        name: form.application_name,
        application_name: form.application_name,
      });
    }
    return list;
  })();

  if (loading) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs items={crumbs} />
            </div>
          </div>
          <div className="st-loading">
            <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
            <p>{t('trLoading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={crumbs} />
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-content">
            <div className="st-block">
              <h2 className="st-block-header">{isChange ? t('plChangeApp') : t('plAddApp')}</h2>
              {isChange ? (
                <div className="st-form-grid pl-app-form">
                  <div className="st-field st-field-wide">
                    <label>{t('pdTeam')}</label>
                    <input className="st-input" value={form.team_name} disabled />
                  </div>
                  <div className="st-field">
                    <label>{t('apaApplication')}</label>
                    <select
                      className="st-select"
                      value={form.application_id}
                      disabled={loadingApps}
                      onChange={(event) => setForm((prev) => ({ ...prev, application_id: event.target.value }))}
                    >
                      <option value="">{loadingApps ? t('trLoading') : t('apaPickApp')}</option>
                      {applicationOptions.map((app) => {
                        const id = app.application_id || app.id;
                        return (
                          <option key={id} value={String(id)}>
                            {applicationOptionLabel(app) || t('mtAppFallback', { id })}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="st-field">
                    <label>{t('pdNumber')}</label>
                    <input
                      type="number"
                      min="0"
                      className="st-input"
                      value={form.number}
                      placeholder={t('apaNumberPh')}
                      onChange={(event) => setForm((prev) => ({ ...prev, number: event.target.value }))}
                    />
                  </div>
                </div>
              ) : (
                <div className="pl-app-split">
                  <div className="st-field">
                    <label>{t('apaSearchTeam')}</label>
                    <TeamPicker
                      champId={champId}
                      isPicked={(id) => String(id) === String(form.team_id)}
                      onPick={(team) => {
                        setForm((prev) => ({
                          ...prev,
                          team_id: String(team.team_id || team.id),
                          team_name: team.name || '',
                        }));
                      }}
                    />
                  </div>
                  <div className="pl-app-fields">
                    <div className="st-field">
                      <label>{t('apaApplication')}</label>
                      <select
                        className="st-select"
                        value={form.application_id}
                        disabled={loadingApps}
                        onChange={(event) => setForm((prev) => ({ ...prev, application_id: event.target.value }))}
                      >
                        <option value="">{loadingApps ? t('trLoading') : t('apaPickApp')}</option>
                        {applicationOptions.map((app) => {
                          const id = app.application_id || app.id;
                          return (
                            <option key={id} value={String(id)}>
                              {applicationOptionLabel(app) || t('mtAppFallback', { id })}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    <div className="st-field">
                      <label>{t('pdNumber')}</label>
                      <input
                        type="number"
                        min="0"
                        className="st-input"
                        value={form.number}
                        placeholder={t('apaNumberPh')}
                        onChange={(event) => setForm((prev) => ({ ...prev, number: event.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {canWrite ? (
        <div className="st-actions">
          <button type="button" className="st-btn st-btn-primary" disabled={saving} onClick={handleSave}>
            <FiSave /> {saving ? t('stSaving') : t('stdSaveChanges')}
          </button>
        </div>
        ) : null}
      </div>
    </div>
  );
}
